/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Locale, LocaleKeys } from "../../model/locale";
import { showToastMessage } from "../toastMessage";
import {
  dismissUnityDialog,
  selectIsUnityDialogVisible,
  selectUnityDialogActions,
  showUnityDialog
} from "../unityDialog";
import type { UnityDialogAction, UnityDialogConfig } from "../unityDialog";
import type { UnityAppDispatch, UnityRootState } from "../UnityAppStore";
import {
  getMyHubErrorToastMessage,
  MyHubErrorMapper
} from "./myHubErrorMapper";

/**
 * Action ids the My Hub screen-failure dialog is raised with. Also how
 * {@link dismissMyHubScreenErrorDialog} recognises the dialog as its own.
 */
export const MY_HUB_ERROR_DISMISS_ACTION_ID: string = "my-hub-error-dismiss";
export const MY_HUB_ERROR_RETRY_ACTION_ID: string = "my-hub-error-retry";

/**
 * The UnityDialog for a My Hub screen that failed to load — the My Hub twin
 * of Home Screen's A707 dialog: the composite code beside the title — the
 * sub-code resolved by the shared `ErrorCodeUtil`, e.g. `A754-G500` — with
 * Dismiss and Retry. An offline failure takes the network copy, so a dropped
 * connection does not read as a backend outage; the code is kept either way.
 *
 * @param error - The raw error the screen's fetch threw
 * @param code - The `ErrorTypes` code for that fetch
 * @param onRetry - Re-runs the failed fetch
 */
export function createMyHubScreenErrorDialogConfig(
  error: unknown,
  code: string,
  onRetry: () => void
): UnityDialogConfig {
  const mapped: MyHubErrorMapper = new MyHubErrorMapper(error, code);
  const isOffline: boolean = mapped.isConnectivityFailure();
  return {
    title: Locale.getString(
      isOffline
        ? LocaleKeys.NETWORK_ERROR_TITLE
        : LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_TITLE
    ),
    message: Locale.getString(
      isOffline
        ? LocaleKeys.NETWORK_ERROR_MESSAGE
        : LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
    ),
    variant: "error",
    errorCode: mapped.getUIErrorwithSubErrorCode(),
    actions: [
      {
        id: MY_HUB_ERROR_DISMISS_ACTION_ID,
        label: Locale.getString(LocaleKeys.ACTION_DISMISS)
      },
      {
        id: MY_HUB_ERROR_RETRY_ACTION_ID,
        label: Locale.getString(LocaleKeys.NETWORK_ERROR_RETRY),
        onPress: onRetry
      }
    ],
    defaultFocusedActionId: MY_HUB_ERROR_DISMISS_ACTION_ID
  };
}

/** Options for {@link reportMyHubScreenFailure}. */
interface TMyHubScreenFailureOptions {
  /**
   * Whether the surface still has content on screen. A failed refresh over
   * content already shown leaves the screen usable, so it gets a coded toast
   * rather than a dialog over the top of it.
   */
  hasContent: boolean;
  /** Re-runs the failed fetch — the dialog's Retry. */
  retry: () => void;
}

/**
 * Surfaces a failed My Hub screen load: the screen-failure dialog when there
 * is nothing on screen, a coded toast when a refresh failed over content.
 * Either way the user is shown the composite code, sub-code included.
 */
export const reportMyHubScreenFailure =
  (error: unknown, code: string, options: TMyHubScreenFailureOptions) =>
  (dispatch: UnityAppDispatch): void => {
    if (options.hasContent) {
      dispatch(showToastMessage(getMyHubErrorToastMessage(error, code)));
      return;
    }
    dispatch(
      showUnityDialog(
        createMyHubScreenErrorDialogConfig(error, code, options.retry)
      )
    );
  };

/**
 * Takes the screen-failure dialog down if it is the one showing. It sits on
 * the app-shell UnityDialog surface, which outlives My Hub, so left standing
 * it would paint a My Hub error over whatever screen comes next. Any other
 * dialog is left alone.
 */
export const dismissMyHubScreenErrorDialog =
  () =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    const state: UnityRootState = getState();
    const isMyHubErrorDialog: boolean =
      selectIsUnityDialogVisible(state) &&
      selectUnityDialogActions(state).some(
        (action: UnityDialogAction): boolean =>
          action.id === MY_HUB_ERROR_DISMISS_ACTION_ID
      );
    if (isMyHubErrorDialog) {
      dispatch(dismissUnityDialog());
    }
  };
